import type { MouseEventHandler, PropsWithChildren, ReactNode } from 'react';

import cn from 'classnames';

import { u } from '../../styles';
import { ThreeDotsVertical } from '../icons';
import { Box, BoxProps, Stack } from '../Layout';
import { Menu, type MenuProps } from '../Menu/Menu';

import { ParameterCardCorridors, ParameterCardSingleCorridor } from './common/Corridors';
import { ParameterCardTitle } from './common/ParameterCardTitle';
import { ParameterCardComparison, ParameterCardValue, ParameterCardValues } from './common/ParameterCardValues';
import * as css from './ParameterCard.css';
import { largeTheme, smallTheme } from './Theme.css';

type ParameterCardMenuProps = Pick<MenuProps, 'menuType'>;

const ParameterCardMenu = ({ children, menuType }: PropsWithChildren<ParameterCardMenuProps>) => (
    <Menu
        label="Parameter actions"
        aria-label="Open parameter actions"
        align="right"
        buttonSize={24}
        menuType={menuType}
        trigger={
            <Box width="max-content" pr={u(2)} mr={-16} pt={u(2)} mt={-16}>
                <ThreeDotsVertical style={{ marginLeft: 8 }} />
            </Box>
        }
        unmountOnHide
        onClick={(event) => event.stopPropagation()}
    >
        {children}
    </Menu>
);

export type ParameterCardProps = Pick<BoxProps, 'shadow'> & {
    small?: boolean;
    compact?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
    corridors?: ReactNode;
};

const ParameterCardRoot = ({
    small,
    compact,
    corridors,
    children,
    onClick,
    ...rest
}: PropsWithChildren<ParameterCardProps>) => {
    return (
        <Stack
            onClick={onClick}
            {...rest}
            bg="raised"
            radius="md"
            gap={onClick ? 0 : u(1)}
            isolate
            className={cn(css.card, { [largeTheme]: !small, [smallTheme]: small })}
        >
            <Stack gap={compact ? u(0.5) : 0} px={compact ? u(1) : u(2)} pt={compact ? u(1) : u(2)}>
                {children}
            </Stack>
            {corridors}
        </Stack>
    );
};

export const ParameterCard = {
    Root: ParameterCardRoot,
    Title: ParameterCardTitle,
    Menu: ParameterCardMenu,
    Values: ParameterCardValues,
    Value: ParameterCardValue,
    Comparison: ParameterCardComparison,
    SingleCorridor: ParameterCardSingleCorridor,
    Corridors: ParameterCardCorridors,
};
